import React, { useState } from 'react';
import { Edit2, Plus, Gauge, Palette, Image as ImageIcon, EyeOff, Eye, X, Check, ChevronLeft, Crown, Trash2, Lock, Link as LinkIcon } from 'lucide-react';
import { translations } from '../translations';
import ServiceLinkPicker from './ServiceLinkPicker';

import '../styles/CarCard.css';
import '../styles/CarSetupForm.css';

const CarCard = ({ cars, selectedCarId, onSelectCar, onUpdateCarName, onAddCar, onEditCar, onDeleteCar, onUpdateMileage, onUpdateCarImage, onUpdateCarBgPosition, onToggleGauge, language = 'en', isPro, onUpgradeClick, onPresentPaywall, distanceUnit = 'km' }) => {
    const selectedCarIndex = cars.findIndex(c => c.id === selectedCarId);
    const selectedCar = cars[selectedCarIndex] || cars[0];
    const isLockedState = !isPro && selectedCarIndex >= 1;

    const [isCustomizing, setIsCustomizing] = useState(false);
    const [isEditingMileage, setIsEditingMileage] = useState(false);
    const [showServicePicker, setShowServicePicker] = useState(false);
    const [mileageInput, setMileageInput] = useState('');

    const [isPistonMode, setIsPistonMode] = useState(() => localStorage.getItem('pistonMode') === 'true');
    const [showProPopup, setShowProPopup] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const t = translations[language];

    // Optional utility to show ads occasionally via Dynamic Import
    const tryShowAd = async () => {
        if (!isPro && Math.random() < 0.25) { // 25% chance of ad on these interactions
            const { showInterstitialAd } = await import('./Ads');
            await showInterstitialAd();
        }
    };

    // Persist piston mode
    React.useEffect(() => {
        localStorage.setItem('pistonMode', isPistonMode);
    }, [isPistonMode]);

    // If no cars exist, render an empty state card
    if (!selectedCar) {
        return (
            <div className="car-card empty-car-card" onClick={onAddCar} style={{
                minHeight: '220px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2px dashed var(--border-color)',
                borderRadius: '24px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-secondary)',
                margin: '1rem',
                transition: 'all 0.2s ease'
            }}>
                <div style={{
                    width: '64px', height: '64px', borderRadius: '50%', background: 'var(--bg-primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                    <Plus size={32} style={{ color: 'var(--accent)' }} />
                </div>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: 'var(--text-primary)', fontWeight: '600' }}>{t?.noCarsAdded || 'No Cars Added'}</h3>
                <p style={{ fontSize: '0.95rem', opacity: 0.8 }}>{t?.tapToAddCar || 'Tap here to add your first car'}</p>
            </div>
        );
    }

    const handleAddCar = async () => {
        if (!isPro && cars.length >= 1) {
            setShowProPopup(true);
            return;
        }
        await tryShowAd();
        onAddCar();
    };

    const handleMileageClick = () => {
        let displayVal = selectedCar.mileage || '';
        if ((distanceUnit === 'miles' || distanceUnit === 'mi') && displayVal) {
            displayVal = Math.round(Number(displayVal) * 0.621371).toString();
        }
        setMileageInput(displayVal);
        setIsEditingMileage(true);
    };

    const handleMileageSave = () => {
        if (mileageInput === '17072007') {
            setIsPistonMode(prev => !prev);
            setIsEditingMileage(false);
            return;
        }

        if (String(mileageInput).trim() !== '') {
            let valToSave = String(mileageInput).trim();
            if (distanceUnit === 'miles' || distanceUnit === 'mi') {
                valToSave = Math.round(Number(valToSave) / 0.621371).toString();
            }
            onUpdateMileage(selectedCar.id, valToSave);
            setIsEditingMileage(false);
        } else {
            setIsEditingMileage(false);
        }
    };

    const handleEditCar = () => {
        onEditCar(selectedCar);
    };

    // Image upload logic
    const handleImageUpload = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.src = reader.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const maxWidth = 800;
                    const scale = maxWidth / img.width;
                    canvas.width = scale < 1 ? maxWidth : img.width;
                    canvas.height = scale < 1 ? img.height * scale : img.height;

                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);

                    onUpdateCarImage(selectedCar.id, type, compressedBase64);
                };
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="car-card">
            <div className="car-card-background" style={{
                backgroundImage: isPro && selectedCar.bgImage ? `url(${selectedCar.bgImage})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: isPro && selectedCar.bgPosition ? `center ${selectedCar.bgPosition}%` : 'center',
                filter: isLockedState ? 'blur(4px)' : 'none',
                opacity: isLockedState ? 0.6 : 1,
            }}>

                {!isLockedState && (
                    isEditingMileage ? (
                        <div className="car-mileage-edit">
                            <input
                                type="number"
                                value={mileageInput}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || (Number(val) <= 100000000 && val.length <= 9)) {
                                        setMileageInput(val);
                                    }
                                }}
                                autoFocus
                                onBlur={handleMileageSave}
                                onKeyPress={(e) => e.key === 'Enter' && handleMileageSave()}
                            />
                            <button onClick={handleMileageSave}><div className="check-icon">✓</div></button>
                        </div>
                    ) : (() => {
                        const baseMileage = parseInt(selectedCar.mileage) || 0;
                        const gpsMileage  = selectedCar.total_mileage_km || 0;
                        const totalKm   = baseMileage + gpsMileage;
                        const isMiles   = distanceUnit === 'miles' || distanceUnit === 'mi';
                        const unit      = isMiles ? 'mi' : 'km';
                        const displayTotal = isMiles
                            ? Math.round(totalKm * 0.621371)
                            : Math.round(totalKm);
                        const hasBase = baseMileage > 0 || gpsMileage > 0;

                        return (
                            <div
                                className="car-mileage-badge"
                                onClick={handleMileageClick}
                                style={{ cursor: 'pointer', opacity: 1, flexDirection: 'column', gap: '2px' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <Gauge size={14} />
                                    <span>
                                        {hasBase
                                            ? `${displayTotal.toLocaleString()} ${unit}`
                                            : (t?.tapToSetMileage || 'Tap to set mileage')}
                                    </span>
                                </div>
                            </div>
                        );
                    })()
                )}
                <div className="car-card-content">
                    <div className="car-info">
                        <div className="car-name-display">
                            <h3 className="car-name" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{selectedCar.name}</h3>
                            <button
                                className="edit-name-btn"
                                onClick={async () => {
                                    await tryShowAd();
                                    handleEditCar();
                                }}
                                title="Edit Car Details"
                            >
                                <Edit2 size={16} />
                            </button>
                            {!isCustomizing && (
                                <button className="edit-name-btn" onClick={async () => {
                                    if (isPro) {
                                        setIsCustomizing(true);
                                    } else {
                                        await tryShowAd();
                                        setShowProPopup(true);
                                    }
                                }} title="Customize Card (CarTrack+)">
                                    <Palette size={16} />
                                </button>
                            )}
                            {!isCustomizing && (
                                <button className="edit-name-btn" onClick={() => setShowServicePicker(true)} title="Link to Auto Service">
                                    <LinkIcon size={16} />
                                </button>
                            )}
                        </div>

                        {/* Car Details */}
                        {selectedCar.brand && (
                            <div className="car-details">
                                <div className="car-detail-row">
                                    <span className="detail-value full-details" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                                        {[
                                            [selectedCar.brand, selectedCar.model, selectedCar.year].filter(Boolean).join(' '),
                                            selectedCar.engine
                                        ].filter(Boolean).join(' • ')}
                                    </span>
                                </div>
                            </div>
                        )}


                    </div>

                    {!selectedCar.hideGauge && (
                        <div className="car-visual">
                            {isPistonMode ? (
                                <svg className="car-wheel-image" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="60" cy="60" r="55" fill="rgba(255,255,255,0.95)" stroke="#333" strokeWidth="2" />
                                    <rect x="40" y="30" width="40" height="40" fill="#e2e8f0" stroke="#475569" strokeWidth="2" rx="2" />
                                    <line x1="40" y1="40" x2="80" y2="40" stroke="#cbd5e1" strokeWidth="2" />
                                    <line x1="40" y1="50" x2="80" y2="50" stroke="#cbd5e1" strokeWidth="2" />
                                    <line x1="40" y1="60" x2="80" y2="60" stroke="#cbd5e1" strokeWidth="2" />
                                    <line x1="60" y1="70" x2="60" y2="100" stroke="#64748b" strokeWidth="12" strokeLinecap="round" />
                                    <circle cx="60" cy="100" r="8" fill="#475569" />
                                    <circle cx="60" cy="50" r="4" fill="#94a3b8" />
                                    <path d="M55 15 L60 25 L65 15" stroke="#fbbf24" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    <circle cx="60" cy="25" r="2" fill="#fbbf24" />
                                </svg>
                            ) : (
                                <svg className="car-wheel-image" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="60" cy="60" r="55" fill="rgba(255,255,255,0.95)" stroke="#333" strokeWidth="2" />
                                    {[0, 30, 60, 90, 120, 150, 180, 210, 240].map((angle, i) => {
                                        const rad = (angle + 150) * Math.PI / 180;
                                        const x1 = 60 + 45 * Math.cos(rad);
                                        const y1 = 60 + 45 * Math.sin(rad);
                                        const x2 = 60 + 38 * Math.cos(rad);
                                        const y2 = 60 + 38 * Math.sin(rad);
                                        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#738ADB" strokeWidth={i % 3 === 0 ? "3" : "2"} className="gauge-tick" />;
                                    })}
                                    <circle cx="60" cy="60" r="6" fill="#738ADB" className="gauge-center" />
                                    <line x1="60" y1="60" x2="60" y2="28" stroke="#738ADB" strokeWidth="3" strokeLinecap="round" className="gauge-needle" />
                                    <text x="60" y="80" textAnchor="middle" fill="#333" fontSize="12" fontWeight="600">{distanceUnit === 'miles' || distanceUnit === 'mi' ? 'mph' : 'km/h'}</text>
                                </svg>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Locked Car Overlay Layer */}
            {isLockedState && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: '60px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    zIndex: 10,
                    padding: '2rem',
                    textAlign: 'center'
                }}>
                    <div style={{
                        width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(0,0,0,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', backdropFilter: 'blur(8px)'
                    }}>
                        <Lock size={32} style={{ color: '#fbbf24' }} />
                    </div>
                    <h3 style={{ fontSize: '1.4rem', marginBottom: '0.5rem', color: 'white', fontWeight: '700', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                        {t?.carLocked || 'Car Locked'}
                    </h3>
                    <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.9)', marginBottom: '1.5rem', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                        {t?.unlockProMsg || 'Upgrade to CarTrack+ to unlock unlimited car profiles.'}
                    </p>
                    <button
                        onClick={() => setShowProPopup(true)}
                        style={{
                            padding: '0.8rem 1.5rem',
                            background: '#738ADB',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '1.05rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}
                    >
                        <Crown size={18} />
                        {t?.unlockNow || 'Unlock Now'}
                    </button>
                    {/* Re-add delete button for locked cars so users can manage them */}
                    <button
                        style={{
                            marginTop: '1.5rem', background: 'transparent', border: 'none',
                            color: 'rgba(255,255,255,0.7)', textDecoration: 'underline',
                            cursor: 'pointer', fontSize: '0.9rem'
                        }}
                        onClick={() => setShowDeleteConfirm(true)}
                    >
                        {t?.deleteThisCar || 'Delete this car'}
                    </button>
                </div>
            )}



            {/* Customization Tab (Overlay) */}
            {isCustomizing && (
                <div className="car-setup-container">
                    <div className="car-setup-background" style={{ zIndex: -1 }}>
                        <div className="setup-blob blob-1"></div>
                        <div className="setup-blob blob-2"></div>
                        <div className="setup-blob blob-3"></div>
                    </div>

                    <div className="car-setup-card edit-mode" style={{ zIndex: 1, padding: '2rem' }}>
                        <div className="setup-header" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <button
                                onClick={() => setIsCustomizing(false)}
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--text-primary)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0.5rem',
                                    borderRadius: '50%',
                                    transition: 'background 0.2s'
                                }}
                                className="back-btn"
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', paddingLeft: '48px', paddingRight: '48px' }}>
                                <div className="setup-icon" style={{ width: '40px', height: '40px', margin: 0 }}>
                                    <Palette size={20} />
                                </div>
                                <h2 className="setup-title" style={{ fontSize: '1.5rem', margin: 0 }}>{t?.cardAppearance || 'Card Appearance'}</h2>
                            </div>
                        </div>

                        <div className="customization-options">
                            <label className="customize-option-card">
                                <div className="option-icon bg-icon"><ImageIcon size={24} /></div>
                                <div className="option-text">
                                    <h5>{t?.customBackground || 'Custom Background'}</h5>
                                    <p>{t?.uploadCarPhoto || 'Upload a photo of your car'}</p>
                                </div>
                                <input type='file' style={{ display: 'none' }} accept="image/*" onChange={(e) => handleImageUpload(e, 'bg')} />
                            </label>

                            {isPro && selectedCar.bgImage && (
                                <>
                                    <button className="customize-option-card" onClick={() => onUpdateCarImage(selectedCar.id, 'bg', null)}>
                                        <div className="option-icon" style={{ color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' }}><Trash2 size={24} /></div>
                                        <div className="option-text">
                                            <h5 style={{ color: '#ef4444' }}>{t?.removeBackground || 'Remove Background'}</h5>
                                            <p>{t?.restoreDefault || 'Restore default appearance'}</p>
                                        </div>
                                    </button>
                                    <div className="bg-position-slider-container">
                                        <div className="bg-position-header">
                                            <p className="position-label">{t?.verticalAlignment || 'Vertical Alignment'} <span className="position-value">{selectedCar.bgPosition || 50}%</span></p>
                                        </div>
                                        <div
                                            className="bg-mini-preview"
                                            style={{
                                                backgroundImage: `url(${selectedCar.bgImage})`,
                                                backgroundPosition: `center ${selectedCar.bgPosition || 50}%`
                                            }}
                                        />
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={selectedCar.bgPosition || 50}
                                            onChange={(e) => onUpdateCarBgPosition(selectedCar.id, e.target.value)}
                                            className="bg-position-slider"
                                        />
                                        <div className="slider-labels">
                                            <span>{t?.posTop || 'Top'}</span>
                                            <span>{t?.posCenter || 'Center'}</span>
                                            <span>{t?.posBottom || 'Bottom'}</span>
                                        </div>
                                    </div>
                                </>
                            )}

                            <button className="customize-option-card" onClick={() => onToggleGauge(selectedCar.id)}>
                                <div className={`option-icon ${(!isPro || !selectedCar.hideGauge) ? 'eye-active' : 'eye-disabled'}`}>
                                    {(!isPro || !selectedCar.hideGauge) ? <Eye size={24} /> : <EyeOff size={24} />}
                                </div>
                                <div className="option-text">
                                    <h5>{t?.visuals || 'Visuals'}</h5>
                                    <p>{(!isPro || !selectedCar.hideGauge) ? (t?.visible || 'Visible') : (t?.hidden || 'Hidden')}</p>
                                </div>
                            </button>
                        </div>

                        <div className="setup-actions">
                            <button className="save-btn" onClick={() => setIsCustomizing(false)}>
                                <Check size={20} />
                                {t?.done || 'Done'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pro Upgrade Popup */}
            {showProPopup && (
                <div className="modal-overlay" onClick={() => setShowProPopup(false)} style={{ zIndex: 1000, alignItems: 'center' }}>
                    <div className="modal-content pro-upgrade-modal" onClick={(e) => e.stopPropagation()} style={{
                        textAlign: 'center',
                        padding: 0,
                        width: '90%',
                        maxWidth: '420px',
                        borderRadius: '28px',
                        margin: '0 auto',
                        animation: 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                        background: 'var(--bg-primary)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        {/* Premium Header Banner */}
                        <div style={{
                            background: 'linear-gradient(180deg, #1f2235 0%, #151624 100%)',
                            padding: '3rem 2rem 2rem',
                            position: 'relative'
                        }}>
                            <button className="modal-close" onClick={() => setShowProPopup(false)} style={{ color: '#ffffff', background: 'rgba(255,255,255,0.1)', top: '16px', right: '16px', border: 'none', width: '32px', height: '32px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                <X size={20} />
                            </button>

                            <div style={{
                                width: '72px', height: '72px', margin: '0 auto 1.5rem',
                                background: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid rgba(251, 191, 36, 0.6)',
                                boxShadow: '0 0 30px rgba(251, 191, 36, 0.15)'
                            }}>
                                <Crown size={36} color="#fbbf24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                            </div>

                            <h2 style={{ fontSize: '1.75rem', margin: '0 0 0.75rem', color: '#ffffff', fontWeight: '800', letterSpacing: '-0.02em' }}>
                                CarTrack<span style={{ color: '#fbbf24' }}>+</span>
                            </h2>
                            <p style={{ color: '#9ca3af', margin: 0, fontSize: '0.95rem', lineHeight: 1.5, padding: '0 0.5rem' }}>
                                {t?.unlockPremiumMsg || 'Unlock premium features and take full control of your car tracking experience.'}
                            </p>
                        </div>

                        {/* Content Area */}
                        <div style={{ padding: '2rem' }}>

                            {/* Pricing Options */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                                {/* Monthly Card */}
                                    <button
                                    onClick={() => {
                                        setShowProPopup(false);
                                        if (onPresentPaywall) onPresentPaywall();
                                        else if (onUpgradeClick) onUpgradeClick({ identifier: '$rc_monthly', priceString: '€3.99' });
                                    }}
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        padding: '1.25rem 0.5rem',
                                        background: 'var(--bg-secondary)',
                                        border: '2px solid transparent',
                                        borderRadius: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        position: 'relative'
                                    }}
                                >
                                    <span style={{ fontSize: '1.15rem', color: 'var(--text-primary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {t?.monthly || 'Monthly'}
                                    </span>
                                </button>

                                {/* Yearly Card (Highlighted) */}
                                <button
                                    onClick={() => {
                                        setShowProPopup(false);
                                        if (onPresentPaywall) onPresentPaywall();
                                        else if (onUpgradeClick) onUpgradeClick({ identifier: '$rc_annual', priceString: '€49.99' });
                                    }}
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        padding: '1.25rem 0.5rem',
                                        background: 'var(--bg-primary)',
                                        border: '2px solid #fbbf24',
                                        borderRadius: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        position: 'relative',
                                        boxShadow: '0 4px 15px rgba(251, 191, 36, 0.1)'
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', top: '-1px', left: '-1px', right: '-1px',
                                        background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)',
                                        color: 'white', fontSize: '0.7rem', fontWeight: 800, padding: '0.3rem',
                                        textTransform: 'uppercase', letterSpacing: '0.05em',
                                        borderTopLeftRadius: '15px', borderTopRightRadius: '15px'
                                    }}>
                                        {t?.bestValue || 'Best Value'}
                                    </div>
                                    <span style={{ fontSize: '1.15rem', color: '#d97706', fontWeight: 800, marginTop: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {t?.yearly || 'Yearly'}
                                    </span>
                                </button>
                            </div>

                            {/* Feature List */}
                            <div style={{ textAlign: 'left', marginBottom: '0.5rem' }}>
                                <h3 style={{ fontSize: '0.85rem', marginBottom: '1.25rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>{t?.premiumFeatures || 'Premium Features:'}</h3>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {(t?.proFeatures || []).map((feature, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', color: 'var(--text-primary)', fontSize: '1rem', lineHeight: 1.4, fontWeight: 500 }}>
                                            <span style={{ fontSize: '1.15rem', flexShrink: 0, marginTop: '0px' }}>{feature.icon}</span>
                                            {feature.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Car Confirmation */}
            {showDeleteConfirm && (
                <div onClick={() => setShowDeleteConfirm(false)} style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, padding: '1rem'
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        background: 'var(--modal-bg)', color: 'var(--header-text)',
                        borderRadius: '20px', padding: '2rem', width: '100%',
                        maxWidth: '340px', textAlign: 'center',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                        animation: 'fadeIn 0.15s ease-out'
                    }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🗑️</div>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 700 }}>
                            {t?.deleteCarTitle ? t.deleteCarTitle(selectedCar.name) : `Delete ${selectedCar.name}?`}
                        </h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
                            {t?.deleteCarBody || 'This will permanently delete the car and all its expenses. This cannot be undone.'}
                        </p>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                style={{
                                    flex: 1, padding: '0.75rem',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: 'var(--radius-md)', background: 'transparent',
                                    color: 'var(--text-primary)', fontWeight: 600,
                                    cursor: 'pointer', fontSize: '0.95rem'
                                }}
                            >
                                {t?.cancel || 'Cancel'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    onDeleteCar(selectedCar.id);
                                }}
                                style={{
                                    flex: 1, padding: '0.75rem',
                                    border: 'none', borderRadius: 'var(--radius-md)',
                                    background: '#ef4444', color: 'white',
                                    fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem'
                                }}
                            >
                                {t?.delete || 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Car Switcher Pills */}
            {cars.length > 0 && (
                <div className="car-switcher">
                    {cars.map((car, index) => {
                        const isLocked = !isPro && index >= 1;
                        return (
                            <div key={car.id} className="car-pill-wrap">
                                <button
                                    className={`car-pill${car.id === selectedCarId ? ' active' : ''}${isLocked ? ' locked' : ''}`}
                                    onClick={async () => {
                                        if (isLocked) {
                                            setShowProPopup(true);
                                        } else {
                                            if (car.id !== selectedCarId) await tryShowAd();
                                            onSelectCar(car.id);
                                        }
                                    }}
                                >
                                    {isLocked ? (
                                        <Lock size={14} className="car-pill-lock" style={{ opacity: 0.7 }} />
                                    ) : (
                                        <span
                                            className="car-pill-dot"
                                            style={{ backgroundColor: car.color || '#738ADB' }}
                                        />
                                    )}
                                    <span className="car-pill-name">{car.name}</span>
                                </button>
                                {cars.length > 1 && !isLocked && (
                                    <button
                                        className="car-pill-delete"
                                        title="Delete car"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onSelectCar(car.id);
                                            setShowDeleteConfirm(true);
                                        }}
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    <button className="car-pill car-pill-add" onClick={handleAddCar} title="Add new car">
                        <Plus size={16} />
                    </button>
                </div>
            )}

            {/* Service Link Picker Modal */}
            {showServicePicker && (
                <ServiceLinkPicker
                    carId={selectedCar.id}
                    onClose={() => setShowServicePicker(false)}
                    language={language}
                />
            )}
        </div>
    );
};

export default CarCard;
